import { useStorage } from '@vueuse/core'

export const useCustomFieldStore = defineStore('customField', () => {
  const customFields = ref<any>()

  // Cached project custom field options in localStorage
  const projectCustomFieldOptions = ref(
    useStorage('project_custom_field_options', [] as any[], localStorage)
  )

  function setData(data: any) {
    customFields.value = {
      ...(customFields.value ?? {}),
      ...data
    }
  }

  function getData(property?: string) {
    return property ? customFields?.value?.[property] : customFields?.value
  }

  /**
   * Fetch project custom field options from API and cache in localStorage
   */
  async function fetchProjectCustomFieldOptions(): Promise<void> {
    try {
      const response = await useApi('custom-fields/projects', 'GET')
      if (response?.data?.custom_fields_0) {
        projectCustomFieldOptions.value = response.data.custom_fields_0
        console.log('[CustomFieldStore] ✅ Cached project custom field options')
      }
    } catch (error) {
      console.error('[CustomFieldStore] ❌ Failed to fetch project custom field options:', error)
    }
  }

  /**
   * Resolve a mapped custom field path to its actual column name
   * Example: 'inspect_app.project.propertyType' -> 'custom_fields_0.s_2' -> 's_2'
   */
  function getCustomFieldColumn(pathOrColumn: string): string {
    // If it's already a column name (like 's_2'), return it directly
    if (pathOrColumn.match(/^[a-z]_\d+$/)) {
      return pathOrColumn
    }

    const mappedPath = getData(pathOrColumn)
    if (mappedPath) {
      // Extract column name from path like 'custom_fields_0.s_2' -> 's_2'
      const parts = mappedPath.split('.')
      return parts[parts.length - 1]
    }

    return pathOrColumn
  }

  /**
   * Ensure project custom field options are loaded, with retry logic
   */
  async function ensureProjectCustomFieldOptions(): Promise<void> {
    if (projectCustomFieldOptions.value.length > 0) {
      return
    }

    console.log('[CustomFieldStore] ⚠️ Options empty, fetching...')
    await fetchProjectCustomFieldOptions()

    // If still empty after first attempt, retry once
    if (projectCustomFieldOptions.value.length === 0) {
      console.log('[CustomFieldStore] ⚠️ First attempt failed, retrying...')
      await new Promise((resolve) => setTimeout(resolve, 1000)) // Wait 1 second
      await fetchProjectCustomFieldOptions()
    }
  }

  /**
   * Ensure custom field mapping data is loaded from tenant/settings
   */
  async function ensureCustomFieldMappings(): Promise<void> {
    // Check if we already have mapping data
    if (customFields.value && Object.keys(customFields.value).length > 0) {
      return
    }

    console.log('[CustomFieldStore] ⚠️ Mapping data empty, fetching from tenant/settings...')

    try {
      const mandantSettings = await useApi('tenant/settings', 'GET')
      setData(mandantSettings?.data ?? {})
      console.log('[CustomFieldStore] ✅ Mapping data loaded')
    } catch (error) {
      console.error('[CustomFieldStore] ❌ Failed to load mapping data:', error)
    }
  }

  /**
   * Get options for a specific custom field
   * Accepts either direct column name (e.g., 's_2') or mapped path (e.g., 'inspect_app.project.propertyType')
   */
  function getCustomFieldOptions(columnOrPath: string): any[] {
    const column = getCustomFieldColumn(columnOrPath)
    const field = projectCustomFieldOptions.value.find((f: any) => f.column === column)
    return field?.options ?? []
  }

  /**
   * Get the text value for a custom field option by ID
   */
  function getCustomFieldOptionValue(column: string, id: number | string): string {
    const options = getCustomFieldOptions(column)
    const option = options.find((o: any) => o.id === Number(id))
    return option?.value ?? ''
  }

  return {
    setData,
    getData,
    projectCustomFieldOptions: readonly(projectCustomFieldOptions),
    fetchProjectCustomFieldOptions,
    ensureProjectCustomFieldOptions,
    ensureCustomFieldMappings,
    getCustomFieldOptions,
    getCustomFieldOptionValue
  }
})
